import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { UserProfile, updateMyProfile } from '../../services/userProfileService';
import { toastHelper } from '../../utils/toastHelper';

interface UserProfileEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: UserProfile;
  onSave?: (updated: UserProfile) => void;
}

export const UserProfileEditDialog: React.FC<UserProfileEditDialogProps> = ({
  open,
  onOpenChange,
  profile,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    display_name: profile.display_name || '',
    bio: profile.bio || '',
    show_email: profile.show_email,
    youtube_url: profile.featured_tune ? `https://youtube.com/watch?v=${profile.featured_tune.youtube_id}` : '',
  });
  const [saving, setSaving] = useState(false);

  // Extract YouTube ID from URL
  const extractYouTubeId = (url: string): string | null => {
    if (!url) return null;

    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate: display name required if email is hidden
    if (!formData.show_email && !formData.display_name.trim()) {
      toastHelper.error('Display name is required when email is hidden');
      return;
    }

    // Extract youtube_id from URL if provided
    let youtubeId: string | null = null;
    if (formData.youtube_url.trim()) {
      youtubeId = extractYouTubeId(formData.youtube_url);
      if (!youtubeId) {
        toastHelper.error('Invalid YouTube URL');
        return;
      }
    }

    setSaving(true);

    try {
      const updated = await updateMyProfile({
        display_name: formData.display_name || null,
        bio: formData.bio || null,
        show_email: formData.show_email,
        featured_tune_youtube_id: youtubeId,
      });

      toastHelper.success('Profile updated successfully');
      onSave?.(updated);
      onOpenChange(false);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to update profile';
      toastHelper.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow alphanumeric and underscores
    const value = e.target.value.replace(/[^a-zA-Z0-9_]/g, '');
    setFormData({ ...formData, display_name: value.slice(0, 50) });
  };

  const handleBioChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData({ ...formData, bio: e.target.value.slice(0, 200) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Profile</DialogTitle>
          <DialogDescription>
            Customize how you appear on the leaderboard
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Display Name */}
          <div className="space-y-2">
            <Label htmlFor="display_name">
              Display Name{!formData.show_email && <span className="text-destructive ml-1">*</span>}
            </Label>
            <Input
              id="display_name"
              placeholder={formData.show_email ? "Your username (optional)" : "Your username (required)"}
              value={formData.display_name}
              onChange={handleDisplayNameChange}
              maxLength={50}
              className={!formData.show_email && !formData.display_name.trim() ? 'border-destructive' : ''}
            />
            <p className="text-xs text-muted-foreground">
              {!formData.show_email && (
                <span className="text-destructive font-medium">Required when email is hidden. </span>
              )}
              Alphanumeric and underscores only, max 50 characters
            </p>
          </div>

          {/* Bio */}
          <div className="space-y-2">
            <Label htmlFor="bio">Bio</Label>
            <Textarea
              id="bio"
              placeholder="Tell others about yourself (optional)"
              value={formData.bio}
              onChange={handleBioChange}
              maxLength={200}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              {formData.bio.length}/200 characters
            </p>
          </div>

          {/* Show Email Toggle */}
          <div className="flex items-center justify-between space-x-2">
            <div className="space-y-0.5">
              <Label htmlFor="show_email">Show Email</Label>
              <p className="text-xs text-muted-foreground">
                Display your email address on the leaderboard
              </p>
            </div>
            <Switch
              id="show_email"
              checked={formData.show_email}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, show_email: checked })
              }
            />
          </div>

          {/* Featured Video URL */}
          <div className="space-y-2">
            <Label htmlFor="youtube_url">Featured Video (YouTube URL)</Label>
            <Input
              id="youtube_url"
              placeholder="https://youtube.com/watch?v=... (optional)"
              value={formData.youtube_url}
              onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              Paste the URL of a video you uploaded through Naptune. Leave empty to remove featured video.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
